import "./App.css";
import { React, useState, useEffect } from "react";
import { useDispatch } from "react-redux";
import {
  BrowserRouter as Router,
  Routes,
  Route,
  Outlet,
  useLocation,
} from "react-router-dom";
import { GoogleOAuthProvider } from "@react-oauth/google";
import embedStore from "./redux/store/embedStore";
import PageHome from "./components/ChannelPages/PageHome";
import ThemeHandler from "./utils/ThemeHandler";
import Onboarding from "./components/Onboarding/Onboarding";
import { Helmet } from "react-helmet";
import { Provider } from "react-redux";
import Logo from "./assets/icons/logo.svg";
import Footer from "./components/Footer/Footer";
import AuthPage from "./components/auth/AuthPage";
import ChannelPage from "./components/Channel/ChannelPage";
import useInitializeApp from "./services/initializeApp";
import AcceptInvite from "./components/widgets/AcceptInvite";
import HomePage from "./components/Home/Home";
import { extractSubdomain } from "./utils/extractDomain";
import { setIsDomain } from "./redux/slices/authSlice";
import QueryPage from "./components/query";
import Page404 from "./components/Page404/Page404";
import { ProtectedOnboardingRoute } from "./components/Onboarding/Protectedonboardroute";
import EmbedHomePage from "./components/EmbedChannels/views/EmbedHomePage";
import PrivacyPage from "./components/Footer/Modals/PrivacyPage";
import ResetPassword from "./utils/ResetPassword";
import EmbedAuthPage from "./components/EmbedChannels/views/EmbedAuthPage";
import EmbedOnboardPage from "./components/EmbedChannels/views/EmbedOnboardPage";
import EmbedProfilePage from "./components/EmbedChannels/views/EmbedProfilePage";
import GoogleAuthPopup from "./components/EmbedChannels/views/EmbedGoogleAuth";
import GoogleAuthCallback from "./components/EmbedChannels/utility/Callback";
import Integration from "./components/Integration/Integration";
import Modals from "./utils/modals";
import AdminHome from "./components/Admin/AdminHome";
import SetupWizard from "./components/Setup/SetupWizard";
import PageDM from "./components/ChannelPages/DMPages/PageDM";
import DMChat from "./components/ChannelPages/DMPages/DMChat";
import DocsPage from "./components/Documentation/DocMainPage";
import DMMessages from "./components/ChannelPages/DMPages/DMMessages";
import ChannelAdmin from "./components/ChannelAdmin/ChannelAdmin";
import TermsPage from "./components/Footer/Modals/TermsPage";
import Shipping from "./components/Footer/Modals/Shipping";
import ContactUs from "./components/Footer/Modals/ContactUs";
import CancellationPolicy from "./components/Footer/Modals/Cancellation";
import EventFullPage from "./components/ChannelPages/widgets/EventFullPage";

const App = () => {
  const dispatch = useDispatch();
  useInitializeApp();
  const [hasSubdomain, setHasSubdomain] = useState(false);

  useEffect(() => {
    const url = new URL(window.location.href);
    const subdomain = extractSubdomain(url);
    if (subdomain) {
      dispatch(setIsDomain(true));
      setHasSubdomain(true);
    }
  }, [dispatch]);

  const MainLayout = () => {
    const location = useLocation();
    const allowedPaths = ["/get-started", "/channels/onboarding"];
    const isFooterVisible = allowedPaths.some((path) => {
      const regex = new RegExp(`^${path.replace(/:[^\s/]+/g, "([^/]+)")}$`);
      return regex.test(location.pathname);
    });

    return (
      <div className="relative flex flex-col min-h-screen bg-theme-primaryBackground w-full overflow-y-auto custom-scrollbar">
        <Helmet>
          <meta charSet="utf-8" />
          <title>Channels.Social</title>
          <link rel="icon" href={Logo} />
        </Helmet>
        <div className="flex flex-1 ">
          <div className=" w-full">
            <Outlet />
          </div>
        </div>
        <div className="w-full">{isFooterVisible && <Footer />}</div>
      </div>
    );
  };

  return (
    <Router>
      <GoogleOAuthProvider
        clientId={
          process.env.REACT_APP_GOOGLE_CLIENT_ID ||
          "391369792833-72medeq5g0o5sklosb58k7c98ps72foj.apps.googleusercontent.com"
        }
      >
        <ThemeHandler />
        <Routes>
          <Route element={<MainLayout />}>
            <Route
              path="/embed/google-auth/login"
              element={<GoogleAuthPopup />}
            />
            <Route path="/admin/channels" element={<ChannelAdmin />} />
            <Route
              path="/admin/channels/:channelId"
              element={<ChannelPage />}
            />
            <Route
              path="/admin/channels/:channelId/topic/:topicId"
              element={<PageHome />}
            />

            <Route
              path="/auth/google/callback"
              element={<GoogleAuthCallback />}
            />
            <Route path="/admin/dashboard" element={<AdminHome />} />
            <Route path="/admin/setup" element={<SetupWizard />} />

            <Route path="/" element={<HomePage />} />
            <Route path="/pricing" element={<HomePage />} />

            <Route
              path="/get-started"
              element={<AuthPage isSubdomain={hasSubdomain} />}
            />

            <Route
              path="/channels/onboarding"
              element={
                <ProtectedOnboardingRoute>
                  <Onboarding />
                </ProtectedOnboardingRoute>
              }
            />

            <Route path="/api/integration/channels" element={<Integration />} />

            <Route path="/accept-invite" element={<AcceptInvite />} />
            <Route path="/privacy-policy" element={<PrivacyPage />} />
            <Route path="/terms-and-conditions" element={<TermsPage />} />
            <Route path="/shipping-policy" element={<Shipping />} />
            <Route path="/contact-us" element={<ContactUs />} />
            <Route
              path="/cancellation-policy"
              element={<CancellationPolicy />}
            />
            <Route path="/query/feedback/channels" element={<QueryPage />} />
            <Route path="/event/:eventId" element={<EventFullPage />} />
            <Route
              path="/reset-password/:token"
              element={<ResetPassword />}
            />
            <Route path="/documentation/channels" element={<DocsPage />} />

            <Route
              path="/embed/channels/*"
              element={
                <Provider store={embedStore}>
                  <EmbedHomePage />
                </Provider>
              }
            >
              <Route
                path="user/:username/channel/:channelId"
                element={<ChannelPage />}
              />
              <Route path="get-started" element={<EmbedAuthPage />} />
              <Route
                path="user/:username/profile"
                element={<EmbedProfilePage />}
              />
              <Route path="user/:username/messages/" element={<PageDM />}>
                <Route path="list" element={<DMMessages />} />
                <Route path="list/:user" element={<DMChat />} />
              </Route>

              <Route path="onboarding" element={<EmbedOnboardPage />} />
              <Route
                path="user/:username/channel/:channelId/c-id/topic/:topicId"
                element={<PageHome />}
              />
              <Route path="event/:eventId" element={<EventFullPage />} />
            </Route>

            <Route path="*" element={<Page404 />} />
          </Route>
        </Routes>
        <Modals />
      </GoogleOAuthProvider>
    </Router>
  );
};

export default App;
